import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@ntconsult/aluma-react';
import { ME_QUERY_KEY } from '@features/auth';

interface AuthErrorScreenProps {
  error: unknown;
}

/**
 * Mostrada quando não foi possível verificar a sessão (ex.: 429 rate limit,
 * 5xx, falha de rede). Evita o redirecionamento indevido para o /login quando
 * o usuário ainda pode ter um token válido.
 */
export function AuthErrorScreen({ error }: AuthErrorScreenProps) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const isRateLimited =
    axios.isAxiosError(error) && error.response?.status === 429;

  const retry = () =>
    queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-surface-raised">
      <div className="text-center max-w-md px-4">
        <AlertTriangle className="w-16 h-16 text-warning mx-auto mb-6" />
        <h1 className="text-xl font-semibold text-foreground mb-2">
          {isRateLimited ? t('rateLimit.title') : t('error.title')}
        </h1>
        <p className="text-foreground-muted mb-8">
          {isRateLimited ? t('rateLimit.message') : t('error.message')}
        </p>
        <Button onClick={retry}>{t('actions.refresh')}</Button>
      </div>
    </div>
  );
}
